import { ChangeEvent, FC, useLayoutEffect, useState } from 'react';
import { Empty, Input, Typography } from 'antd';

import { useTasksStore } from '../../modules/business-logic/Tasks';
import { SearchIcon } from '../../components/Icons';
import { LoadingData } from '../../components/LoadingData';
import { TasksTable } from './components/Table';
import { ErrorLoad } from '../../components/ErrorLoad';
import { useDebounce } from '../../hooks/useDebounce';
import { assertNever, AsyncStatus } from '../../types';

import styles from './styles.module.css';

const { Text } = Typography;

export const TaskList: FC = () => {
    const [value, setValue] = useState('');
    const { tasksState, search } = useTasksStore();
    const debouncedSearch = useDebounce(search, 500);

    useLayoutEffect(() => {
        // Runs before paint (unlike useEffect), so a stale prior search result never
        // flashes for a frame when navigating back here from TaskView.
        search('');
    }, [search]);

    const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        debouncedSearch(nextValue);
        if (nextValue.trim() === '') {
            search('');
        }
    };

    const renderResult = () => {
        switch (tasksState.status) {
            case AsyncStatus.IDLE:
                return (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className={styles.empty}
                        description={<Text className={styles.placeholder}>Введите номер или название задания</Text>}
                    />
                );
            case AsyncStatus.LOADING:
                return null;
            case AsyncStatus.ERROR:
                return (
                    <ErrorLoad
                        title={'Не удалось загрузить данные'}
                        description={'Проверьте подключение к интернету и попробуйте обновить страницу'}
                        actionLabel={'Обновить страницу'}
                        onAction={() => search(value)}
                    />
                );
            case AsyncStatus.SUCCESS:
                return tasksState.data.length > 0 ? (
                    <TasksTable tasks={tasksState.data} />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className={styles.empty}
                        description={<Text className={styles.placeholder}>Задание не найдено</Text>}
                    />
                );
            default:
                return assertNever(tasksState);
        }
    };

    return (
        <div className={styles.content}>
            <div className={styles.pageTitle}>Задания</div>
            <section className={styles.tableContent}>
                <Input
                    placeholder={'Поиск'}
                    suffix={<SearchIcon width={20} height={20} />}
                    className={styles.input}
                    value={value}
                    onChange={onSearch}
                />
                <LoadingData isLoading={tasksState.status === AsyncStatus.LOADING}>{renderResult()}</LoadingData>
            </section>
        </div>
    );
};
